// src/components/MemberPhotoAvatar.js
import React, { useState, useEffect } from 'react';
import { Image } from 'react-bootstrap';
import { ref, getDownloadURL } from 'firebase/storage'; // Import Firebase storage functions
import { storage } from '../firebase'; // Assuming your firebase.js exports 'storage'

const DEFAULT_MEMBER_PLACEHOLDER_URL = '/Person.png';

function MemberPhotoAvatar({ src, alt, size = 40, cacheKey }) { // Added cacheKey for potential future use
    const [imageUrl, setImageUrl] = useState(DEFAULT_MEMBER_PLACEHOLDER_URL);
    const [isLoading, setIsLoading] = useState(true); // To show a simple loading state if needed

    useEffect(() => {
        let isMounted = true;
        setImageUrl(DEFAULT_MEMBER_PLACEHOLDER_URL); // Reset to placeholder initially or on src change
        setIsLoading(true);

        const resolveSrc = async () => {
            if (!src || src.trim() === '') {
                if (isMounted) {
                    setImageUrl(DEFAULT_MEMBER_PLACEHOLDER_URL);
                    setIsLoading(false);
                }
                return;
            }

            // Check if src is already an HTTP/HTTPS URL
            if (src.startsWith('http://') || src.startsWith('https://')) {
                if (isMounted) {
                    setImageUrl(src);
                    setIsLoading(false);
                }
            } else {
                // Assume it's a GCS path and try to get the download URL
                try {
                    const storageRef = ref(storage, src);
                    const downloadUrl = await getDownloadURL(storageRef);
                    if (isMounted) {
                        setImageUrl(downloadUrl);
                    }
                } catch (error) {
                    if (error.code !== 'storage/object-not-found') {
                        console.warn(`Error getting download URL for GCS path "${src}":`, error);
                    }
                    // Fallback to placeholder if GCS path resolution fails
                    if (isMounted) {
                        setImageUrl(DEFAULT_MEMBER_PLACEHOLDER_URL);
                    }
                } finally {
                    if (isMounted) {
                        setIsLoading(false);
                    }
                }
            }
        };

        resolveSrc();

        return () => {
            isMounted = false;
        };
    }, [src, cacheKey]); // Re-run effect if src or cacheKey changes

    const handleError = (e) => {
        // Check if the current src is already the placeholder to prevent infinite loops
        const currentFullPlaceholderUrl = window.location.origin + DEFAULT_MEMBER_PLACEHOLDER_URL;
        if (e.target.src !== currentFullPlaceholderUrl) {
            e.target.onerror = null;
            e.target.src = DEFAULT_MEMBER_PLACEHOLDER_URL;
        } else {
            console.warn("Placeholder image failed to load:", DEFAULT_MEMBER_PLACEHOLDER_URL);
        }
    };

    // While isLoading is true and the imageUrl is still the placeholder,
    // you could optionally show a spinner, but Image's onError handles visual fallback.
    // For simplicity, we let the Image component attempt to load imageUrl.

    return (
        <Image
            src={imageUrl} // Use the resolved or placeholder URL from state
            alt={alt || 'Member photo'}
            roundedCircle
            style={{
                width: `${size}px`,
                height: `${size}px`,
                objectFit: 'cover',
                border: '1px solid #dee2e6' // Optional: adds a light border
            }}
            onError={handleError}
        />
    );
}

export default MemberPhotoAvatar;
